import React, { useEffect, useState, useCallback, useRef } from "react";
import { Routes, Route, Link, useParams, NavLink } from "react-router-dom";
import { api, API_BASE, getToken, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { PaginationBar } from "@/components/PaginationBar";
import { WidgetError } from "@/components/WidgetError";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { FileText, Upload, ChevronRight, CreditCard, Repeat, Ban } from "lucide-react";

/**
 * Stage 3A — Truck Stop tenant's SaaS billing (Platform → Tenant).
 * Fully separate from the tenant→customer finance module.
 * Only truckstop_owner sees these routes (enforced on the backend too).
 */
export default function TenantBillingModule() {
  return (
    <div className="min-w-0">
      <div className="mb-4">
        <div className="label-overline">Account</div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Billing</h1>
        <p className="text-slate-600 mt-1 text-sm">
          Your TruckStop SaaS platform bills. This is separate from any invoices you send your own customers.
        </p>
      </div>

      <div className="flex gap-2 border-b border-slate-200 mb-4 overflow-x-auto">
        {[
          { to: "/app/billing", label: "Overview", exact: true },
          { to: "/app/billing/invoices", label: "Invoices" },
          { to: "/app/billing/payments", label: "Payments" },
        ].map((t) => (
          <NavLink key={t.to} to={t.to} end={t.exact}
                   data-testid={`billing-tab-${t.label.toLowerCase()}`}
                   className={({ isActive }) =>
                     "flex items-center gap-2 px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 " +
                     (isActive ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t.label}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route index element={<MyBilling />} />
        <Route path="invoices" element={<MyInvoices />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="payments" element={<MyPayments />} />
      </Routes>
    </div>
  );
}

function MyBilling() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let c = false;
    setError(null);
    api.get("/billing/me")
       .then(({ data }) => { if (!c) setData(data); })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [reloadKey]);

  if (error) return <WidgetError error={error} title="Could not load billing"
                                 onRetry={() => setReloadKey((k) => k + 1)}
                                 testId="billing-me-error" />;
  if (!data) return <div className="label-overline text-slate-500">Loading…</div>;

  const suspended = data.tenant.status === "suspended";
  const grace = data.tenant.status === "grace";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        {suspended && (
          <div className="bg-red-50 border border-red-300 rounded p-4"
               data-testid="billing-suspended-banner">
            <div className="label-overline text-red-800">Account suspended</div>
            <p className="mt-1 text-sm text-slate-700">
              Your truck stop operations are paused until your outstanding invoice(s) are settled.
              You can still upload proof of payment below to reactivate the account.
            </p>
          </div>
        )}
        {grace && (
          <div className="bg-amber-50 border border-amber-300 rounded p-4"
               data-testid="billing-grace-banner">
            <div className="label-overline text-amber-800">Grace period</div>
            <p className="mt-1 text-sm text-slate-700">
              Please settle your invoice before <b>{data.tenant.grace_until && fmtDate(data.tenant.grace_until)}</b> to avoid suspension.
            </p>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded p-5">
          <div className="label-overline">Outstanding</div>
          <div className="text-4xl font-black font-mono mt-2 text-red-700"
               data-testid="billing-outstanding">
            {fmtMoney(data.outstanding_cents)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Across {data.unpaid_invoice_count} unpaid invoice{data.unpaid_invoice_count === 1 ? "" : "s"}
          </div>
          <Link to="/app/billing/invoices"
                className="mt-3 inline-flex items-center text-blue-700 font-semibold text-sm">
            View invoices <ChevronRight size={14} />
          </Link>
        </div>

        <div className="bg-white border border-slate-200 rounded p-5">
          <div className="label-overline mb-3">Bank details — manual EFT</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="label-overline text-slate-500">Bank</div><div className="font-mono">{data.bank_details.bank_name}</div></div>
            <div><div className="label-overline text-slate-500">Account name</div><div className="font-mono long-value">{data.bank_details.account_name}</div></div>
            <div><div className="label-overline text-slate-500">Account #</div><div className="font-mono">{data.bank_details.account_number}</div></div>
            <div><div className="label-overline text-slate-500">Branch code</div><div className="font-mono">{data.bank_details.branch_code}</div></div>
          </div>
          <p className="mt-3 text-xs text-slate-600">{data.bank_details.reference_hint}</p>
        </div>
      </div>

      <div className="space-y-4">
        <AutopayCard data={data} onChange={() => setReloadKey((k) => k + 1)} />
        <div className="bg-white border border-slate-200 rounded p-5">
          <div className="label-overline">Plan</div>
          <div className="text-xl font-black mt-1">
            {(data.tenant.plan || "—").replace(/_/g, " ")}
          </div>
          <div className="text-xs text-slate-500 font-mono mt-1">
            {data.tenant.billing_cycle}
          </div>
          <div className="mt-3">
            <div className="label-overline">Price</div>
            <div className="font-mono text-lg">
              {fmtMoney((data.tenant.price_cents || 0) - (data.tenant.discount_cents || 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 3B — Autopay status card + cancellation modal
// ---------------------------------------------------------------------------

function AutopayCard({ data, onChange }) {
  const settings = data.settings || {};
  const state = settings.billing_state || (settings.autopay_enabled ? "active" : null);
  const [showCancel, setShowCancel] = useState(false);

  // No autopay yet — show a small explainer with a link to the invoice detail.
  if (!settings.autopay_enabled && state !== "cancellation_pending") {
    return (
      <div className="bg-white border border-slate-200 rounded p-5"
           data-testid="billing-autopay-inactive">
        <div className="flex items-center gap-2">
          <Repeat size={14} className="text-slate-500" />
          <div className="label-overline">Autopay</div>
        </div>
        <div className="text-sm mt-2 text-slate-600">
          Not enabled. When paying an open invoice you can choose &ldquo;Pay
          &amp; enable autopay&rdquo; to authorise recurring billing via PayFast.
        </div>
      </div>
    );
  }

  const pending = state === "cancellation_pending";
  const cancelled = state === "cancelled";

  return (
    <div className={"bg-white border rounded p-5 " +
      (cancelled ? "border-slate-300" :
       pending ? "border-amber-300" : "border-green-300")}
         data-testid="billing-autopay-status">
      <div className="flex items-center gap-2">
        <Repeat size={14} className={cancelled ? "text-slate-500" :
                                     pending ? "text-amber-600" : "text-green-600"} />
        <div className="label-overline">Autopay</div>
      </div>
      <div className="text-sm mt-2 font-semibold" data-testid="billing-autopay-state">
        {cancelled ? "Cancelled" :
         pending ? "Cancellation pending" : "Active"}
      </div>
      {settings.cancellation_effective_at && (
        <div className="text-xs text-slate-600 mt-1">
          Access remains active until <b>{fmtDate(settings.cancellation_effective_at)}</b>.
        </div>
      )}
      {settings.subscription_cancelled_at && (
        <div className="text-xs text-slate-500 mt-1">
          Cancelled on {fmtDate(settings.subscription_cancelled_at)}
        </div>
      )}
      {!cancelled && (
        <Button variant="outline" onClick={() => setShowCancel(true)}
                data-testid="billing-autopay-cancel-btn"
                className="mt-3 w-full rounded border-red-300 text-red-700 hover:bg-red-50">
          <Ban size={14} className="mr-2" />
          {pending ? "Retry cancellation" : "Cancel autopay"}
        </Button>
      )}
      <CancelAutopayDialog open={showCancel}
                            onClose={() => setShowCancel(false)}
                            onDone={() => { setShowCancel(false); onChange(); }} />
    </div>
  );
}

function CancelAutopayDialog({ open, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/billing/autopay/cancel",
                    { reason: reason.trim() || null });
      toast.success("Autopay cancellation submitted");
      onDone();
    } catch (e) {
      toast.error(formatError(e, "Cancellation failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="billing-autopay-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Cancel autopay?</DialogTitle>
          <DialogDescription>
            Cancelling stops future automatic billing. Your account remains
            active until the end of the paid period. Outstanding invoices
            remain payable. Your data is retained according to our Terms
            and Privacy Policy.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="label-overline">Reason (optional)</Label>
          <Input value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 data-testid="billing-autopay-cancel-reason"
                 placeholder="e.g. moving to a different plan"
                 className="rounded h-10" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="billing-autopay-cancel-close"
                  className="rounded">Keep autopay</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="billing-autopay-cancel-confirm"
                  className="rounded bg-red-600 hover:bg-red-700">
            {submitting ? "Cancelling…" : "Yes, cancel autopay"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyInvoices() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    let c = false;
    setError(null);
    api.get("/billing/invoices", { params: { page, limit: 20 } })
       .then(({ data }) => { if (!c) { setItems(data.items); setPages(data.pages); } })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [page]);

  if (error) return <WidgetError error={error} title="Could not load invoices" testId="billing-inv-error" />;

  return (
    <div className="bg-white border border-slate-200 rounded overflow-hidden">
      <div className="overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Invoice #</th>
              <th className="text-left px-4 py-3">Period</th>
              <th className="text-left px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Due</th>
              <th className="text-left px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No invoices yet</td></tr>
            )}
            {items.map((i) => (
              <tr key={i.id} className="border-b border-slate-100" data-testid={`billing-inv-${i.invoice_no}`}>
                <td className="px-4 py-2 font-mono">{i.invoice_no}</td>
                <td className="px-4 py-2 font-mono text-xs">{i.period_start} → {i.period_end}</td>
                <td className="px-4 py-2 font-mono">{fmtMoney(i.total_cents)}</td>
                <td className="px-4 py-2">
                  <span className={"inline-block px-2 py-0.5 text-[10px] font-mono font-bold uppercase border rounded " + (
                    i.status === "paid" ? "bg-green-100 text-green-800 border-green-200" :
                    i.status === "part_paid" ? "bg-amber-100 text-amber-800 border-amber-200" :
                    i.status === "overdue" ? "bg-red-100 text-red-800 border-red-200" :
                    "bg-blue-100 text-blue-800 border-blue-200"
                  )}>{i.status}</span>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{i.due_at ? fmtDate(i.due_at) : "—"}</td>
                <td className="px-4 py-2">
                  <Link to={`/app/billing/invoices/${i.id}`}
                        className="text-blue-700 hover:underline text-xs font-semibold">Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} pages={pages} onChange={setPage} />
    </div>
  );
}

function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [popFile, setPopFile] = useState(null);
  const [amount, setAmount] = useState("");
  const [bankRef, setBankRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let c = false;
    setError(null);
    api.get(`/billing/invoices/${id}`)
       .then(({ data }) => {
         if (c) return;
         setInv(data);
         const outstanding = (data.total_cents - (data.paid_cents || 0)) / 100;
         setAmount(outstanding > 0 ? outstanding.toFixed(2) : "");
       })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [id, reloadKey]);

  const openPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/billing/invoices/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { toast.error(formatError(e, "PDF failed")); }
  };

  const submitPop = async (e) => {
    e.preventDefault();
    if (!popFile) return toast.error("Attach a proof of payment file");
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (!cents || cents <= 0) return toast.error("Amount must be positive");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", popFile);
      fd.append("amount_cents", String(cents));
      if (bankRef) fd.append("bank_ref", bankRef);
      await api.post(`/billing/invoices/${id}/proof-of-payment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Proof of payment submitted — awaiting approval");
      setPopFile(null);
      setBankRef("");
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(formatError(err, "Upload failed"));
    } finally { setSubmitting(false); }
  };

  if (error) return <WidgetError error={error} title="Could not load invoice"
                                 onRetry={reload} testId="billing-inv-detail-error" />;
  if (!inv) return <div className="label-overline text-slate-500">Loading…</div>;

  const outstanding = inv.total_cents - (inv.paid_cents || 0);
  const canPay = ["issued", "part_paid", "overdue"].includes(inv.status);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-overline">Invoice</div>
            <div className="text-2xl font-black font-mono mt-1">{inv.invoice_no}</div>
            <div className="text-sm text-slate-600 mt-1">
              {inv.period_start} → {inv.period_end}
            </div>
          </div>
          <Button data-testid="billing-inv-pdf-btn" onClick={openPdf} variant="outline"
                  className="rounded">
            <FileText size={14} className="mr-1" /> View PDF
          </Button>
        </div>
        <table className="w-full mt-6 text-sm">
          <thead className="border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="text-left py-2">Description</th>
              <th className="text-right py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2 long-value">{l.description}</td>
                <td className="py-2 text-right font-mono">{fmtMoney(l.line_total_cents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-black text-lg">
              <td className="pt-3 text-right">Total</td>
              <td className="pt-3 text-right font-mono">{fmtMoney(inv.total_cents)}</td>
            </tr>
            {inv.paid_cents > 0 && (
              <tr className="text-green-700">
                <td className="text-right label-overline">Paid</td>
                <td className="text-right font-mono">{fmtMoney(inv.paid_cents)}</td>
              </tr>
            )}
            {outstanding > 0 && (
              <tr className="text-red-700 font-black">
                <td className="text-right label-overline">Outstanding</td>
                <td className="text-right font-mono">{fmtMoney(outstanding)}</td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      <div className="space-y-4">
        {canPay && (
          <PayOnlineCard invoiceId={id} outstanding={outstanding} />
        )}
        {canPay ? (
          <form onSubmit={submitPop} className="bg-white border border-slate-200 rounded p-5 space-y-3"
                data-testid="billing-pop-form">
            <div className="label-overline">Upload proof of payment</div>
            <p className="text-xs text-slate-600">
              Once approved by the platform, your account is reactivated automatically.
            </p>
            <div>
              <Label className="label-overline">Amount paid (ZAR)</Label>
              <Input required type="number" step="0.01" min="0.01"
                     data-testid="billing-pop-amount"
                     value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="mt-1 rounded h-10 font-mono" />
            </div>
            <div>
              <Label className="label-overline">Bank ref (optional)</Label>
              <Input value={bankRef} onChange={(e) => setBankRef(e.target.value)}
                     data-testid="billing-pop-ref"
                     placeholder="e.g. FNB deposit 0102"
                     className="mt-1 rounded h-10 font-mono" />
            </div>
            <div>
              <Label className="label-overline">Proof file (JPG/PNG/PDF)</Label>
              <input ref={fileRef} type="file"
                     accept="image/*,application/pdf"
                     onChange={(e) => setPopFile(e.target.files?.[0] || null)}
                     data-testid="billing-pop-file"
                     className="mt-1 block text-sm" />
              {popFile && (
                <div className="mt-1 text-xs text-slate-600 truncate">
                  {popFile.name} · {(popFile.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>
            <Button type="submit" disabled={submitting}
                    data-testid="billing-pop-submit"
                    className="w-full rounded bg-blue-700 hover:bg-blue-800">
              <Upload size={14} className="mr-2" />
              {submitting ? "Uploading…" : "Submit for approval"}
            </Button>
          </form>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded p-5 text-sm text-slate-600">
            This invoice is <b>{inv.status}</b> — no payment action required.
          </div>
        )}
      </div>
    </div>
  );
}

function MyPayments() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    let c = false;
    setError(null);
    api.get("/billing/payments", { params: { page, limit: 20 } })
       .then(({ data }) => { if (!c) { setItems(data.items); setPages(data.pages); } })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [page]);

  if (error) return <WidgetError error={error} title="Could not load payments" testId="billing-pay-error" />;

  return (
    <div className="bg-white border border-slate-200 rounded overflow-hidden">
      <div className="overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Ref</th>
              <th className="text-left px-4 py-3">Method</th>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">When</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No payments yet</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`billing-pay-${p.payment_ref}`}>
                <td className="px-4 py-2 font-mono text-xs">{p.payment_ref}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.method}</td>
                <td className="px-4 py-2 font-mono">{p.invoice_no || "—"}</td>
                <td className="px-4 py-2 font-mono">{fmtMoney(p.amount_cents)}</td>
                <td className="px-4 py-2">
                  <span className={"inline-block px-2 py-0.5 text-[10px] font-mono font-bold uppercase border rounded " + (
                    p.status === "approved" ? "bg-green-100 text-green-800 border-green-200" :
                    p.status === "pending" ? "bg-amber-100 text-amber-800 border-amber-200" :
                    p.status === "rejected" ? "bg-red-100 text-red-800 border-red-200" :
                    "bg-slate-100 text-slate-700 border-slate-200"
                  )}>{p.status}</span>
                  {p.status === "rejected" && p.rejected_reason && (
                    <div className="text-[10px] text-red-700 mt-1 long-value">{p.rejected_reason}</div>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{fmtDate(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} pages={pages} onChange={setPage} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Stage 3B — PayOnlineCard: initiates PayFast checkout (one-off or subscription).
// Never marks the invoice paid client-side — that is exclusively the ITN's job.
// ---------------------------------------------------------------------------

function PayOnlineCard({ invoiceId, outstanding }) {
  const [busy, setBusy] = useState(false);
  const [showAutopay, setShowAutopay] = useState(false);

  const postAndRedirect = async (endpoint, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, body);
      const form = document.createElement("form");
      form.method = "post";
      form.action = data.process_url;
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setBusy(false);
      toast.error(formatError(e, "Could not start PayFast checkout"));
    }
  };

  const oneOff = () => postAndRedirect("/billing/checkout/payfast",
                                       { invoice_id: invoiceId });

  return (
    <div className="bg-white border border-blue-300 rounded p-5 space-y-3"
         data-testid="billing-pay-online-card">
      <div className="flex items-center gap-2">
        <CreditCard size={16} className="text-blue-700" />
        <div className="label-overline text-blue-900">Pay online — PayFast</div>
      </div>
      <p className="text-xs text-slate-600">
        Card, instant EFT, or SnapScan. Amount:&nbsp;
        <b className="font-mono">{fmtMoney(outstanding)}</b>
      </p>
      <Button onClick={oneOff} disabled={busy}
              data-testid="billing-pay-online-btn"
              className="w-full rounded bg-blue-700 hover:bg-blue-800">
        <CreditCard size={14} className="mr-2" />
        {busy ? "Redirecting…" : "Pay this invoice"}
      </Button>
      <Button variant="outline" onClick={() => setShowAutopay(true)}
              disabled={busy}
              data-testid="billing-pay-online-autopay-btn"
              className="w-full rounded border-blue-300 text-blue-700 hover:bg-blue-50">
        <Repeat size={14} className="mr-2" />
        Pay &amp; enable autopay
      </Button>
      <AutopayEnableDialog
        open={showAutopay} onClose={() => setShowAutopay(false)}
        onProceed={(terms) => postAndRedirect("/billing/autopay/enable",
                                              { invoice_id: invoiceId, terms })}
        outstanding={outstanding} />
    </div>
  );
}

function AutopayEnableDialog({ open, onClose, onProceed, outstanding }) {
  const [termsInfo, setTermsInfo] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setAccepted(false); return; }
    setLoading(true);
    api.get("/billing/terms")
       .then(({ data }) => setTermsInfo(data))
       .catch(() => setTermsInfo(null))
       .finally(() => setLoading(false));
  }, [open]);

  const proceed = () => {
    if (!accepted || !termsInfo) return;
    onProceed({ accepted: true, terms_version: termsInfo.terms_version });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="billing-autopay-enable-dialog">
        <DialogHeader>
          <DialogTitle>Enable autopay</DialogTitle>
          <DialogDescription>
            After paying this invoice ({fmtMoney(outstanding)}), PayFast will
            automatically charge the same card on your billing cycle. You can
            cancel autopay at any time from the Billing page.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="label-overline text-slate-500">Loading terms…</div>}

        {termsInfo && (
          <div className="space-y-3">
            <div className="text-xs text-slate-600 leading-relaxed">
              Please review the following before continuing:
              <ul className="mt-2 list-disc pl-5 space-y-1">
                <li><a href={termsInfo.links.terms} target="_blank" rel="noreferrer"
                       data-testid="autopay-link-terms"
                       className="text-blue-700 hover:underline">Terms &amp; Conditions</a></li>
                <li><a href={termsInfo.links.privacy} target="_blank" rel="noreferrer"
                       data-testid="autopay-link-privacy"
                       className="text-blue-700 hover:underline">Privacy Policy</a></li>
                <li><a href={termsInfo.links.cancellation} target="_blank" rel="noreferrer"
                       data-testid="autopay-link-cancellation"
                       className="text-blue-700 hover:underline">Cancellation Policy</a></li>
              </ul>
            </div>
            <label className="flex items-start gap-2 pt-2 border-t border-slate-200"
                   data-testid="autopay-terms-checkbox-label">
              <Checkbox checked={accepted}
                        onCheckedChange={(v) => setAccepted(v === true)}
                        data-testid="autopay-terms-checkbox"
                        className="mt-1" />
              <span className="text-xs text-slate-700 leading-relaxed">
                {termsInfo.recurring_authorisation_wording}
              </span>
            </label>
            <div className="text-[11px] text-slate-500 font-mono">
              Terms version: {termsInfo.terms_version}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="autopay-enable-close"
                  className="rounded">Cancel</Button>
          <Button onClick={proceed}
                  disabled={!accepted || !termsInfo}
                  data-testid="autopay-enable-confirm"
                  className="rounded bg-blue-700 hover:bg-blue-800">
            Continue to PayFast
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
